import { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { Fingerprint, RefreshCw, Search, Calendar, FileSpreadsheet, Eye } from 'lucide-react';
import * as XLSX from 'xlsx';
import { Card } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Badge } from '@/shared/components/ui/Badge';
import { Spinner } from '@/shared/components/ui/Spinner';
import { Table, type Column } from '@/shared/components/ui/Table';
import { Modal } from '@/shared/components/ui/Modal';
import { api, getApiErrorMessage } from '@/shared/api/axios';

export interface HardwareUser {
  biometric_id: string;
  name: string;
  total_days: number;
  present_days?: number;
  absent_days?: number;
  leave_days?: number;
  holiday_days?: number;
  total_punches: number;
  total_hours?: number;
}

export interface HardwareDateRow {
  id: string;
  biometric_id: string;
  name: string;
  date: string;
  day_name?: string;
  first_punch: string;
  last_punch: string;
  total_hours: string;
  total_punches: number;
  all_punches: string;
  status?: 'present' | 'absent' | 'on_leave' | 'holiday' | 'weekend';
  details?: string;
}

export function BiometricDateWiseCard({ inModal = false }: { inModal?: boolean } = {}) {
  const [hwUsers, setHwUsers] = useState<HardwareUser[]>([]);
  const [hwRows, setHwRows] = useState<HardwareDateRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewingUser, setViewingUser] = useState<HardwareUser | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<string>(() =>
    new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }).slice(0, 7)
  );

  const loadDateWiseReport = async (month?: string) => {
    try {
      setLoading(true);
      const targetMonth = month || selectedMonth;
      const { data } = await api.get<{ users: HardwareUser[]; rows: HardwareDateRow[]; month: string }>(
        `/api/attendance/biometric/date-wise-report?month=${targetMonth}`
      );
      setHwUsers(data.users || []);
      setHwRows(data.rows || []);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to load biometric attendance report.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDateWiseReport(selectedMonth);
  }, [selectedMonth]);

  const handleExportExcel = () => {
    if (hwUsers.length === 0) {
      toast.error('No biometric data available to export.');
      return;
    }

    try {
      const wb = XLSX.utils.book_new();

      // Sheet 1: Monthly Summary of all biometric employees
      const summaryRows = hwUsers.map((u) => ({
        'Biometric ID': `#${u.biometric_id}`,
        'Employee Name': u.name,
        'Evaluated Days': u.total_days || 0,
        'Days Present': u.present_days || 0,
        'Days Absent': u.absent_days || 0,
        'On Leave': u.leave_days || 0,
        'Holidays': u.holiday_days || 0,
        'Total Punches': u.total_punches || 0,
        'Total Working Hours': `${u.total_hours || 0} hrs`,
      }));

      const summaryWs = XLSX.utils.json_to_sheet(summaryRows);
      XLSX.utils.book_append_sheet(wb, summaryWs, 'Monthly Summary');

      // Individual Sheets per employee (e.g. Uzef, Siddhi, Sandesh, Shoaib, Asnain, Sufiyan, etc.)
      const usedSheetNames = new Set<string>();

      // Group rows by biometric_id
      const rowsByUser = new Map<string, HardwareDateRow[]>();
      for (const row of hwRows) {
        if (!rowsByUser.has(row.biometric_id)) {
          rowsByUser.set(row.biometric_id, []);
        }
        rowsByUser.get(row.biometric_id)!.push(row);
      }

      for (const u of hwUsers) {
        const userRows = rowsByUser.get(u.biometric_id) || [];
        const excelRows = userRows.map((r) => ({
          'Date': r.date,
          'Day': r.day_name || '',
          'Status': formatStatusText(r),
          'Check-In': r.first_punch,
          'Check-Out': r.last_punch,
          'Working Hours': r.total_hours === '-' ? '0 hrs' : `${r.total_hours} hrs`,
          'All Punches (IST)': r.all_punches,
          'Notes / Details': r.details || '',
        }));

        const ws = XLSX.utils.json_to_sheet(excelRows);

        let sheetName = u.name.trim().replace(/[\\/:?*[\]]/g, '_').slice(0, 30);
        if (!sheetName) sheetName = `ID_${u.biometric_id}`;

        let counter = 1;
        let finalSheetName = sheetName;
        while (usedSheetNames.has(finalSheetName.toLowerCase())) {
          finalSheetName = `${sheetName.slice(0, 27)}_${counter}`;
          counter++;
        }
        usedSheetNames.add(finalSheetName.toLowerCase());

        XLSX.utils.book_append_sheet(wb, ws, finalSheetName);
      }

      XLSX.writeFile(wb, `Biometric_Attendance_Report_${selectedMonth}.xlsx`);
      toast.success(`Exported biometric attendance workbook with ${hwUsers.length + 1} sheets.`);
    } catch (err) {
      console.error(err);
      toast.error('Failed to export biometric Excel workbook.');
    }
  };

  const filteredUsers = useMemo(() => {
    if (!searchQuery) return hwUsers;
    const q = searchQuery.toLowerCase();
    return hwUsers.filter(
      (u) =>
        u.name.toLowerCase().includes(q) ||
        u.biometric_id.toLowerCase().includes(q)
    );
  }, [hwUsers, searchQuery]);

  const userColumns: Column<HardwareUser>[] = [
    {
      key: 'name',
      header: 'EMPLOYEE NAME & ID',
      render: (u) => (
        <div className="flex items-center gap-2">
          <span className="font-semibold text-on-surface">{u.name}</span>
          <span className="rounded-full bg-primary/10 px-2.5 py-0.5 font-mono text-body-xs font-bold text-primary">
            ID #{u.biometric_id}
          </span>
        </div>
      ),
    },
    {
      key: 'present',
      header: 'PRESENT DAYS',
      render: (u) => (
        <span className="inline-flex items-center font-bold text-success-text">
          {u.present_days ?? 0} / {u.total_days ?? 0} days
        </span>
      ),
    },
    {
      key: 'absent',
      header: 'ABSENT DAYS',
      render: (u) => {
        const count = u.absent_days ?? 0;
        return (
          <Badge tone={count > 0 ? 'danger' : 'neutral'}>
            {count} day{count !== 1 ? 's' : ''}
          </Badge>
        );
      },
    },
    {
      key: 'leave',
      header: 'ON LEAVE',
      render: (u) => {
        const count = u.leave_days ?? 0;
        return (
          <Badge tone={count > 0 ? 'info' : 'neutral'}>
            {count} day{count !== 1 ? 's' : ''}
          </Badge>
        );
      },
    },
    {
      key: 'hours',
      header: 'TOTAL HOURS',
      align: 'right',
      render: (u) => (
        <span className="font-mono font-semibold text-on-surface">
          {u.total_hours != null && u.total_hours > 0 ? `${u.total_hours}h` : '—'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'PUNCHES LOG',
      align: 'center',
      render: (u) => (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setViewingUser(u)}
          className="h-8 gap-1 text-xs"
        >
          <Eye className="h-3.5 w-3.5" />
          View Month
        </Button>
      ),
    },
  ];

  const userLogs = useMemo(() => {
    if (!viewingUser) return [];
    return hwRows.filter((r) => r.biometric_id === viewingUser.biometric_id);
  }, [hwRows, viewingUser]);

  const content = (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="border-b border-outline bg-primary/5 px-5 py-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Fingerprint className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-data-lg font-semibold text-on-surface">
                Biometric Hardware Attendance (HR Roster View)
              </h2>
              <p className="text-body-xs text-on-surface-variant">
                Live attendance records from eSSL biometric machine (192.0.2.10). Employee roster format.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <span className="rounded-lg bg-surface px-2.5 py-1 text-body-xs font-semibold text-on-surface border border-outline">
              {hwUsers.length} Employees
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => loadDateWiseReport(selectedMonth)}
              disabled={loading}
              className="border-primary/30 text-primary hover:bg-primary/5"
            >
              <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={handleExportExcel}
              className="border-success/40 text-success-text hover:bg-success/10 dark:border-success/40 dark:text-success-text dark:hover:bg-success"
            >
              <FileSpreadsheet className="mr-1.5 h-3.5 w-3.5 text-success-text dark:text-success-text" />
              Export to Excel
            </Button>
          </div>
        </div>
      </div>

      {/* Toolbar Filters */}
      <div className="p-5 flex flex-col gap-4">
        <div className="flex items-center gap-3 flex-wrap">
          {/* Month Selector Filter */}
          <div className="flex items-center gap-1.5 rounded-lg border border-outline bg-surface px-3 py-2">
            <Calendar className="h-4 w-4 text-primary" />
            <input
              type="month"
              value={selectedMonth === 'ALL' ? '' : selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value || 'ALL')}
              className="bg-transparent text-body-sm font-semibold text-on-surface focus:outline-none cursor-pointer"
            />
          </div>

          <Button
            variant={selectedMonth === 'ALL' ? 'primary' : 'outline'}
            size="sm"
            onClick={() =>
              setSelectedMonth(
                selectedMonth === 'ALL'
                  ? new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }).slice(0, 7)
                  : 'ALL'
              )
            }
            className="h-[38px] text-xs font-semibold"
          >
            {selectedMonth === 'ALL' ? 'Showing All Logs' : 'All Time Logs'}
          </Button>

          {/* Search Bar */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-on-surface-variant" />
            <Input
              placeholder="Search employee name or ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex h-64 items-center justify-center">
            <Spinner />
          </div>
        ) : (
          <Table
            columns={userColumns}
            data={filteredUsers}
            rowKey={(u) => u.biometric_id}
            empty="No biometric employees match your search."
          />
        )}
      </div>

      {/* Modal: View Full Month Daily Punches for Selected Employee */}
      {viewingUser && (
        <BiometricUserLogsModal
          user={viewingUser}
          month={selectedMonth}
          logs={userLogs}
          onClose={() => setViewingUser(null)}
        />
      )}
    </div>
  );

  if (inModal) {
    return content;
  }

  return <Card className="overflow-hidden border border-primary/20 shadow-sm">{content}</Card>;
}

function formatStatusText(r: HardwareDateRow): string {
  if (r.status === 'present') return 'Present';
  if (r.status === 'on_leave') return r.details || 'On Leave';
  if (r.status === 'holiday') return r.details || 'Holiday';
  if (r.status === 'weekend') return r.details || 'Weekend';
  return 'Absent';
}

function BiometricUserLogsModal({
  user,
  month,
  logs,
  onClose,
}: {
  user: HardwareUser;
  month: string;
  logs: HardwareDateRow[];
  onClose: () => void;
}) {
  const columns: Column<HardwareDateRow>[] = [
    {
      key: 'date',
      header: 'DATE',
      render: (r) => (
        <div className="flex flex-col">
          <span className="font-medium text-on-surface">{r.date}</span>
          {r.day_name && <span className="text-[10px] text-on-surface-variant font-semibold">{r.day_name}</span>}
        </div>
      ),
    },
    {
      key: 'first_punch',
      header: 'CHECK-IN',
      render: (r) => {
        if (r.status === 'absent') return <Badge tone="danger">Absent</Badge>;
        if (r.status === 'on_leave') return <Badge tone="info">{r.details || 'On Leave'}</Badge>;
        if (r.status === 'holiday') return <Badge tone="warning">{r.details || 'Holiday'}</Badge>;
        if (r.status === 'weekend') return <Badge tone="neutral">{r.details || 'Weekend'}</Badge>;

        return (
          <span className="rounded-md bg-success/15 px-2 py-1 font-mono text-body-xs font-semibold text-success-text">
            {r.first_punch}
          </span>
        );
      },
    },
    {
      key: 'last_punch',
      header: 'CHECK-OUT',
      render: (r) => (
        <span
          className={`rounded-md px-2 py-1 font-mono text-body-xs font-semibold ${
            r.last_punch !== '-' ? 'bg-info/15 text-info-text' : 'text-on-surface-variant'
          }`}
        >
          {r.last_punch}
        </span>
      ),
    },
    {
      key: 'total_hours',
      header: 'WORKING HOURS',
      render: (r) => {
        const hrs = parseFloat(r.total_hours);
        return (
          <span
            className={`inline-flex rounded-full px-2.5 py-0.5 font-mono text-body-xs font-bold ${
              isNaN(hrs)
                ? 'bg-surface-variant text-on-surface-subtle'
                : hrs >= 8
                ? 'bg-success/15 text-success-text'
                : 'bg-warning/15 text-warning-text'
            }`}
          >
            {r.total_hours === '-' ? '-' : `${r.total_hours} hrs`}
          </span>
        );
      },
    },
  ];

  return (
    <Modal
      open
      onClose={onClose}
      title={`Biometric Punch Log — ${user.name} (#${user.biometric_id}) [${month}]`}
      size="lg"
      footer={<Button onClick={onClose}>Close</Button>}
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-outline bg-surface-variant/30 px-4 py-3 text-body-sm">
          <div>
            <span className="font-bold text-on-surface">{user.name}</span>
            <span className="ml-2 font-mono text-xs text-primary font-bold">ID #{user.biometric_id}</span>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <span className="text-success-text font-semibold">Present: {user.present_days}d</span>
            <span className="text-danger-text font-semibold">Absent: {user.absent_days}d</span>
            <span className="text-info-text font-semibold">Leave: {user.leave_days}d</span>
            <span className="text-on-surface font-semibold">Hours: {user.total_hours}h</span>
          </div>
        </div>

        <Table columns={columns} data={logs} rowKey={(r) => r.id} empty="No biometric punch records for this month." />
      </div>
    </Modal>
  );
}
